/**
 * Planner: a set of Events a Team member adds together for one audience,
 * e.g. "Term 1 activities". A Draft keeps its rows on the Planner; sending
 * turns each row into an ordinary Event (see docs/adr/0002).
 */
import { DEFAULT_EVENT_REMINDER_MODES } from "../../constants/eventReminderOptions";
import {
  eventDayProblem,
  eventEndProblem,
  eventEndTimeProblem,
  eventMessageProblem,
  eventPlaceProblem,
  eventTimeProblem,
  rsvpDeadlineProblem,
  titleProblem,
} from "../notices/noticeRules";
import { toLocalDay } from "../events/eventRecurrence";
import {
  buildNoticePayload,
  type NoticeUploadedFile,
  type NoticeWirePayload,
} from "../notices/noticePayload";
import type { Notice } from "../../services/api/types";

export const PLANNER_TITLE_MAX_LENGTH = 100;

/** The most rows a Draft holds (the backend's limit too). Sending allows fewer. */
export const PLANNER_DRAFT_ROWS_MAX = 200;

export type PlannerStatus = "draft" | "sending" | "sent";

/** Who gets a Planner, in a Notice's shape: "circle" is Everyone, "subcircle" is one Circle, "" is not chosen yet. */
export type PlannerAudience = "" | "circle" | "subcircle";

/**
 * One Event in a Draft. Fields use an Event's names so the backend can check a
 * row as it is. A Draft may be unfinished, so any field can be "".
 */
export type PlannerRow = {
  /** Stable across saves; sending derives the Event's ID from it. */
  rowId: string;
  title: string;
  /** YYYY-MM-DD */
  eventStartDate: string;
  /** "9:00" or "18:30" */
  eventStartTime: string;
  /** YYYY-MM-DD; "" when the Event is on one day. */
  eventEndDate: string;
  eventEndTime: string;
  eventLocation: string;
  /** The Event's description, as plain text. */
  body: string;
  /** null uses the Planner's shared default; [] means no Event reminders. */
  eventReminderModes: string[] | null;
  /** Files already uploaded, as Add Event holds them, e.g. an excursion's consent form. */
  uploadedFiles: NoticeUploadedFile[];
  rsvpEnabled: boolean;
  /** When RSVPs close, as an ISO date and time; "" for no deadline. */
  rsvpClosesAt: string;
};

/** The parts of a row a problem can be on, in the order they appear. */
export const PLANNER_ROW_FIELDS = [
  "title",
  "eventStartDate",
  "eventStartTime",
  "eventEndDate",
  "eventEndTime",
  "eventLocation",
  "body",
  "uploadedFiles",
  "rsvpClosesAt",
] as const;

export type PlannerRowField = (typeof PLANNER_ROW_FIELDS)[number];

export const isPlannerRowField = (name: string): name is PlannerRowField =>
  (PLANNER_ROW_FIELDS as readonly string[]).includes(name);

/** Each field's problem, in Add Event's words. */
export type PlannerRowProblems = Partial<Record<PlannerRowField, string>>;

/** What a Team member edits at the top of a Planner. */
export type PlannerDetails = {
  title: string;
  audience: PlannerAudience;
  targetSubcircle: string;
  /** The shared Remind members default for every row; [] means no Event reminders. */
  eventReminderModes: string[];
};

/** Everything Save as Draft sends. */
export type PlannerDraft = PlannerDetails & { rows: PlannerRow[] };

/** A Planner as the server stores it. */
export type SavedPlanner = PlannerDetails & {
  plannerId: string;
  organizationId: string;
  status: PlannerStatus;
  /** A Draft's Event rows. */
  rows: PlannerRow[];
  /** The Events a sent Planner created. */
  noticeIds: string[];
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
};

export type PlannerDetailsProblems = Partial<Record<keyof PlannerDetails, string>>;

const STATUS_LABELS: Record<PlannerStatus, string> = {
  draft: "Draft",
  sending: "Sending",
  sent: "Sent",
};

export const newPlannerDetails = (): PlannerDetails => ({
  title: "",
  audience: "",
  targetSubcircle: "",
  eventReminderModes: [...DEFAULT_EVENT_REMINDER_MODES],
});

export const plannerDetails = (planner: SavedPlanner): PlannerDetails => ({
  title: planner.title,
  audience: planner.audience,
  targetSubcircle: planner.targetSubcircle,
  eventReminderModes: [...planner.eventReminderModes],
});

export const isSamePlannerDetails = (a: PlannerDetails, b: PlannerDetails) =>
  a.title.trim() === b.title.trim() &&
  a.audience === b.audience &&
  a.targetSubcircle === b.targetSubcircle &&
  a.eventReminderModes.length === b.eventReminderModes.length &&
  a.eventReminderModes.every((mode) => b.eventReminderModes.includes(mode));

const TITLE_TOO_LONG = `Keep the title to ${PLANNER_TITLE_MAX_LENGTH} characters or fewer.`;

/** A Draft may be unfinished; only a too-long title stops it saving. */
export const plannerDetailsProblems = (details: PlannerDetails): PlannerDetailsProblems =>
  details.title.trim().length > PLANNER_TITLE_MAX_LENGTH
    ? { title: TITLE_TOO_LONG }
    : {};

/** Why a sent Planner's new title can't be saved; undefined when it can. It only shows in the dashboard now. */
export const plannerRenameProblem = (title: string): string | undefined => {
  const trimmed = title.trim();
  if (!trimmed) return "Give the Planner a title.";
  if (trimmed.length > PLANNER_TITLE_MAX_LENGTH) return TITLE_TOO_LONG;
  return undefined;
};

export const plannerDisplayTitle = (title: string) => title.trim() || "Untitled Planner";

export const plannerStatusLabel = (status: PlannerStatus) => STATUS_LABELS[status];

/**
 * A Draft or Sending Planner counts its rows. A sent Planner counts its Events
 * among the Organization's Notices, so one deleted anywhere drops out; until
 * the Notices load, it counts the Events it created.
 */
export const plannerEventCount = (planner: SavedPlanner, notices?: Notice[]) => {
  if (planner.status !== "sent") return planner.rows.length;
  if (!notices) return planner.noticeIds.length;
  return notices.filter(isLiveEventIn(planner.plannerId)).length;
};

/** A Notice in the Planner that hasn't been deleted. */
export const isLiveEventIn = (plannerId: string) => (notice: Notice) =>
  notice.plannerId === plannerId && !notice.isDeleted;

export const eventCountLabel = (count: number) =>
  `${count} ${count === 1 ? "event" : "events"}`;

/**
 * "Term 1 activities · Draft · 0 events". A send that didn't finish shows how
 * far it got: "Term 1 activities · Sending · 12 of 30 events added".
 */
export const plannerListLabel = (planner: SavedPlanner, notices?: Notice[]) =>
  [
    plannerDisplayTitle(planner.title),
    plannerStatusLabel(planner.status),
    planner.status === "sending" && planner.noticeIds.length > 0
      ? `${planner.noticeIds.length} of ${eventCountLabel(planner.rows.length)} added`
      : eventCountLabel(plannerEventCount(planner, notices)),
  ].join(" · ");

const SENDING_AGAIN_IS_SAFE = "Nothing is added twice, and parents get one notification.";

/**
 * Beside Send on a Planner whose send didn't finish (it stays Sending until every
 * Event exists). With no count recorded (e.g. it timed out), some may still have been added.
 */
export const plannerUnfinishedSendHint = (planner: SavedPlanner) =>
  planner.noticeIds.length === 0
    ? `Sending didn't finish. Send again to add any of the ${eventCountLabel(planner.rows.length)} that are missing. ${SENDING_AGAIN_IS_SAFE}`
    : `Sending didn't finish: ${planner.noticeIds.length} of ${eventCountLabel(planner.rows.length)} were added. Send again to add the rest. ${SENDING_AGAIN_IS_SAFE}`;

export const plannersNewestFirst = (planners: SavedPlanner[]) =>
  [...planners].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

export const newPlannerRow = (rowId: string): PlannerRow => ({
  rowId,
  title: "",
  eventStartDate: "",
  eventStartTime: "",
  eventEndDate: "",
  eventEndTime: "",
  eventLocation: "",
  body: "",
  eventReminderModes: null,
  uploadedFiles: [],
  rsvpEnabled: false,
  rsvpClosesAt: "",
});

/** A copy under a new row ID, e.g. for a weekly fixture that only needs a new date. */
export const duplicatePlannerRow = (row: PlannerRow, rowId: string): PlannerRow => ({
  ...row,
  rowId,
  eventReminderModes: row.eventReminderModes && [...row.eventReminderModes],
  uploadedFiles: row.uploadedFiles.map((file) => ({ ...file })),
});

/** Nothing typed or chosen yet. */
export const isBlankPlannerRow = (row: PlannerRow) =>
  JSON.stringify({ ...row, rowId: "" }) === JSON.stringify(newPlannerRow(""));

/** The Event reminders a row's Event gets: its own choice, otherwise the shared default. */
export const rowEventReminderModes = (row: PlannerRow, sharedDefault: string[]): string[] => [
  ...(row.eventReminderModes ?? sharedDefault),
];

/** The row fields the Events table keeps in a panel under the row, rather than in a column. */
const PLANNER_ROW_EXTRA_FIELDS: PlannerRowField[] = ["body", "uploadedFiles", "rsvpClosesAt"];

/** A problem is on a field in the row's panel, so the panel should be open to show it. */
export const rowExtrasHaveProblems = (problems: PlannerRowProblems) =>
  PLANNER_ROW_EXTRA_FIELDS.some((field) => Boolean(problems[field]));

/**
 * What a row's panel holds, shown beside the row while the panel is closed:
 * "Description", "2 attachments", "RSVP", then its own Event reminders if it has them.
 */
export const plannerRowExtrasSummary = (row: PlannerRow): string[] => {
  const files = row.uploadedFiles.length;
  return [
    row.body.trim() ? "Description" : "",
    files > 0 ? `${files} ${files === 1 ? "attachment" : "attachments"}` : "",
    row.rsvpEnabled ? "RSVP" : "",
    row.eventReminderModes === null
      ? ""
      : row.eventReminderModes.length === 0
        ? "No Event reminders"
        : "Own Event reminders",
  ].filter(Boolean);
};

/** Checks a row with the same rules the Add Event wizard uses. Empty when it's ready to send. */
export const plannerRowProblems = (row: PlannerRow, now: Date = new Date()): PlannerRowProblems => {
  const dates = {
    eventStartDate: toLocalDay(row.eventStartDate),
    eventStartTime: row.eventStartTime,
    eventEndDate: toLocalDay(row.eventEndDate),
    eventEndTime: row.eventEndTime,
  };
  const problems: PlannerRowProblems = {
    title: titleProblem(row.title),
    eventStartDate: eventDayProblem(dates.eventStartDate, now),
    eventStartTime: eventTimeProblem(row.eventStartTime),
    eventEndDate: eventEndProblem(dates),
    eventEndTime: eventEndTimeProblem(dates),
    eventLocation: eventPlaceProblem(row.eventLocation),
    body: eventMessageProblem(row.body),
    rsvpClosesAt: rsvpDeadlineProblem({ ...row, ...dates }),
  };
  for (const field of Object.keys(problems) as PlannerRowField[]) {
    if (!problems[field]) delete problems[field];
  }
  return problems;
};

const DATE_TIME_FIELDS: PlannerRowField[] = [
  "eventStartDate",
  "eventStartTime",
  "eventEndDate",
  "eventEndTime",
];

/**
 * The problems to show on a row: every one once the row is checked (it has been
 * saved or reopened), otherwise only those on fields the Team member has left.
 * An end before the start shows once any date or time has been left.
 */
export const visibleRowProblems = (
  problems: PlannerRowProblems,
  shown: "all" | PlannerRowField[],
): PlannerRowProblems => {
  if (shown === "all") return { ...problems };
  const left = new Set(shown);
  if (DATE_TIME_FIELDS.some((field) => left.has(field))) {
    left.add("eventEndDate");
    left.add("eventEndTime");
  }
  const visible: PlannerRowProblems = {};
  for (const field of Object.keys(problems) as PlannerRowField[]) {
    if (left.has(field)) visible[field] = problems[field];
  }
  return visible;
};

/** A Draft's rows as stored; rows saved before attachments and RSVP have neither. */
export const plannerRows = (planner: SavedPlanner): PlannerRow[] =>
  planner.rows.map((row) =>
    duplicatePlannerRow(
      {
        ...row,
        uploadedFiles: row.uploadedFiles ?? [],
        rsvpEnabled: row.rsvpEnabled ?? false,
        rsvpClosesAt: row.rsvpClosesAt ?? "",
      },
      row.rowId,
    ),
  );

/** Same rows, in the same order, with the same fields. */
export const isSamePlannerRows = (a: PlannerRow[], b: PlannerRow[]) =>
  JSON.stringify(a) === JSON.stringify(b);

/** The most Events one Planner sends (the backend's limit too); a year should be split into terms. */
export const PLANNER_SEND_ROWS_MAX = 100;

/** One row's Event as Send hands it to the server: what Add Event would send, plus the row ID. */
export type PlannerEventPayload = NoticeWirePayload & { rowId: string };

/**
 * Each row's Event, built with Add Event's own payload building so a Planner
 * Event is exactly what Add Event would send. The server adds the Planner's
 * audience and link; the audience is filled in here too, as Add Event would.
 */
export const plannerEventPayloads = (
  details: PlannerDetails,
  rows: PlannerRow[],
  /** Where Add Event files attachments in Drive: the audience's folder. */
  context: { defaultDriveFolderKey?: string } = {},
): PlannerEventPayload[] =>
  rows.map((row) => {
    const eventReminderModes = rowEventReminderModes(row, details.eventReminderModes);
    return {
      rowId: row.rowId,
      ...buildNoticePayload({
        title: row.title,
        body: row.body.trim(),
        type: "event",
        eventStartDate: toLocalDay(row.eventStartDate),
        eventStartTime: row.eventStartTime,
        eventEndDate: toLocalDay(row.eventEndDate),
        eventEndTime: row.eventEndTime,
        eventLocation: row.eventLocation,
        eventReminderModes,
        eventReminderMode: eventReminderModes[0] || "off",
        targetSubcircle: details.audience === "subcircle" ? details.targetSubcircle : "",
        uploadedFiles: row.uploadedFiles,
        rsvpEnabled: row.rsvpEnabled,
        rsvpClosesAt: row.rsvpClosesAt,
      }, context),
    };
  });

/** Why Send is unavailable, in words the Team member can act on; undefined when it can be sent. */
export const plannerSendProblem = (
  details: PlannerDetails,
  rows: PlannerRow[],
  now: Date = new Date(),
): string | undefined => {
  if (!details.title.trim()) return "Give the Planner a title to send it.";
  if (!details.audience) return "Choose who gets it to send it.";
  if (rows.length === 0) return "Add an Event to send the Planner.";
  if (rows.length > PLANNER_SEND_ROWS_MAX) {
    return `A Planner can send up to ${PLANNER_SEND_ROWS_MAX} Events, and this one has ${rows.length}. Split the year into terms, with a Planner for each.`;
  }
  const withProblems = rowsWithProblems(rows, now);
  if (withProblems.length > 0) {
    return `Fix ${namedEvents(withProblems)} to send the Planner.`;
  }
  return undefined;
};

/** The rows that can't be sent yet, with their place in the Planner (1-based). */
export const rowsWithProblems = (
  rows: PlannerRow[],
  now: Date = new Date(),
): { row: PlannerRow; number: number }[] =>
  rows
    .map((row, index) => ({ row, number: index + 1 }))
    .filter(({ row }) => Object.keys(plannerRowProblems(row, now)).length > 0);

const NAMED_EVENTS_MAX = 3;

/** "Event 3 (Deepavali), Event 7 and 4 more": which rows, so they can be found in a long table. */
const namedEvents = (rows: { row: PlannerRow; number: number }[]) => {
  const names = rows.slice(0, NAMED_EVENTS_MAX).map(({ row, number }) => {
    const title = row.title.trim();
    return title ? `Event ${number} (${title})` : `Event ${number}`;
  });
  const more = rows.length - names.length;
  if (more > 0) return `${names.join(", ")} and ${more} more`;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const startMinutes = (time: string) => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/** The Event the push comes from: the earliest by start date and time, then row order. */
export const earliestPlannerRow = (rows: PlannerRow[]): PlannerRow | undefined =>
  rows.reduce<PlannerRow | undefined>((earliest, row) => {
    if (!earliest) return row;
    const byDate = row.eventStartDate.localeCompare(earliest.eventStartDate);
    return byDate < 0 ||
      (byDate === 0 && startMinutes(row.eventStartTime) < startMinutes(earliest.eventStartTime))
      ? row
      : earliest;
  }, undefined);

const dayMonth = (day: Date, withYear: boolean) =>
  day.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
  });

/** "3 Feb – 28 Mar": from the first Event's start to the last one's end. */
export const plannerDateRange = (rows: PlannerRow[]): string => {
  const days = rows
    .flatMap((row) => [toLocalDay(row.eventStartDate), toLocalDay(row.eventEndDate)])
    .filter((day): day is Date => day !== null)
    .sort((a, b) => a.getTime() - b.getTime());
  if (days.length === 0) return "";
  const first = days[0];
  const last = days[days.length - 1];
  const withYear = first.getFullYear() !== last.getFullYear();
  return first.getTime() === last.getTime()
    ? dayMonth(first, false)
    : `${dayMonth(first, withYear)} – ${dayMonth(last, withYear)}`;
};

/** What parents' one push says: "Term 1 activities — 30 events added to your calendar". */
export const plannerPushText = (title: string, eventCount: number) =>
  `${title.trim()} — ${eventCountLabel(eventCount)} added to your calendar`;

/**
 * What a failed Send tells the Team member, including whether to try again. A
 * dropped connection or server failure may have added some Events; sending
 * again finishes the job without duplicates. Anything else (e.g. the Planner
 * changed elsewhere) won't be fixed by sending again, so the server's reason shows.
 * That sending again is safe is said once, beside Send (plannerUnfinishedSendHint).
 */
export const plannerSendFailureMessage = (error: unknown): string =>
  plannerWriteFailureMessage(error, {
    rowProblems: "Nothing was sent. Fix the problems shown on the Events and send again.",
    unfinished: "Sending didn't finish. Send again to finish it.",
    refused: "Couldn't send the Planner.",
  });

/**
 * Sorts a failed Send or Add Event: the server found problems on rows; it may
 * not have finished (a dropped connection or server failure, where trying again
 * is safe); or it refused, so its reason shows after `refused`.
 */
const plannerWriteFailureMessage = (
  error: unknown,
  messages: { rowProblems: string; unfinished: string; refused: string },
): string => {
  const failure = error as { status?: number; responseBody?: unknown; message?: string } | null;
  if (Object.keys(plannerRowProblemsFromServer(failure?.responseBody)).length > 0) {
    return messages.rowProblems;
  }
  if (typeof failure?.status !== "number" || failure.status >= 500) return messages.unfinished;
  return `${messages.refused}${failure.message ? ` ${failure.message}` : " Please try again."}`;
};

/** A problem the server found on one row, as Send returns it. */
export type PlannerServerRowProblems = {
  rowId: string;
  problems: { field: string; message: string }[];
};

/**
 * The row problems in a failed Send's response body, by row ID, under the field each belongs to. A
 * problem on a field the row doesn't show goes under its title.
 */
export const plannerRowProblemsFromServer = (
  responseBody: unknown,
): Record<string, PlannerRowProblems> => {
  const rowProblems = (responseBody as { rowProblems?: PlannerServerRowProblems[] } | null)
    ?.rowProblems;
  const byRow: Record<string, PlannerRowProblems> = {};
  for (const { rowId, problems } of Array.isArray(rowProblems) ? rowProblems : []) {
    const row: PlannerRowProblems = {};
    for (const { field, message } of problems) {
      const shownOn = isPlannerRowField(field) ? field : "title";
      row[shownOn] ??= message;
    }
    byRow[rowId] = row;
  }
  return byRow;
};

/** Where a sent Planner's Event sits in the term: over, the next one coming up, or after that. */
export type SentPlannerEventTiming = "past" | "next" | "later";

export type SentPlannerEvent = { notice: Notice; timing: SentPlannerEventTiming };

/** An Event's when and where: a sent Event (a Notice) or a Draft's row both have these. */
export type PlannerEventFacts = Pick<
  Notice,
  "title" | "eventStartDate" | "eventStartTime" | "eventEndDate" | "eventEndTime" | "eventLocation"
>;

/** A local day plus "H:MM", as a moment. */
const atTime = (day: Date, time: string) => {
  const moment = new Date(day);
  moment.setMinutes(startMinutes(time));
  return moment;
};

/** When an Event is over: its end time, otherwise the end of its last day. */
const eventEndsAt = (notice: PlannerEventFacts): Date | null => {
  const lastDay = toLocalDay(notice.eventEndDate) ?? toLocalDay(notice.eventStartDate);
  if (!lastDay) return null;
  if (notice.eventEndTime) return atTime(lastDay, notice.eventEndTime);
  return new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + 1);
};

const eventStartsAt = (notice: PlannerEventFacts): number => {
  const day = toLocalDay(notice.eventStartDate);
  return day ? atTime(day, notice.eventStartTime ?? "").getTime() : Number.POSITIVE_INFINITY;
};

/**
 * A sent Planner's Events: the Organization's Notices that carry its ID, in date
 * order. They come from the Notices rather than the Planner's stored IDs, so an
 * Event deleted anywhere drops out. Events already over are past; the first one
 * still to come (or still going on) is next.
 */
export const sentPlannerEvents = (
  notices: Notice[],
  plannerId: string,
  now: Date = new Date(),
): SentPlannerEvent[] => {
  const events = notices
    .filter(isLiveEventIn(plannerId))
    .sort((a, b) => eventStartsAt(a) - eventStartsAt(b));
  let nextFound = false;
  return events.map((notice) => {
    const endsAt = eventEndsAt(notice);
    if (endsAt && endsAt <= now) return { notice, timing: "past" };
    if (nextFound) return { notice, timing: "later" };
    nextFound = true;
    return { notice, timing: "next" };
  });
};

/**
 * The Planners list's Next event: the first Event that isn't over. A sent
 * Planner's comes from its Events on the Calendar; a Draft's from its dated rows.
 */
export const plannerNextEvent = (
  planner: SavedPlanner,
  notices: Notice[] = [],
  now: Date = new Date(),
): PlannerEventFacts | undefined => {
  if (planner.status === "sent") {
    return sentPlannerEvents(notices, planner.plannerId, now).find((e) => e.timing === "next")
      ?.notice;
  }
  return planner.rows
    .filter((row) => toLocalDay(row.eventStartDate))
    .filter((row) => {
      const endsAt = eventEndsAt(row);
      return !endsAt || endsAt > now;
    })
    .sort((a, b) => eventStartsAt(a) - eventStartsAt(b))[0];
};

/** "Tue 3 Nov", with the year when it isn't this year. */
const eventDay = (day: Date, now: Date) =>
  `${day.toLocaleDateString("en-GB", { weekday: "short" })} ${dayMonth(day, day.getFullYear() !== now.getFullYear())}`;

/** "Tue 3 Nov, 18:00–20:00", or for a date range "Mon 2 Nov, 9:00 – Wed 4 Nov, 12:00". */
export const plannerEventWhen = (notice: Notice, now: Date = new Date()): string => {
  const startDay = toLocalDay(notice.eventStartDate);
  if (!startDay) return "";
  const start = [eventDay(startDay, now), notice.eventStartTime].filter(Boolean).join(", ");
  const endDay = toLocalDay(notice.eventEndDate);
  if (endDay && endDay.getTime() !== startDay.getTime()) {
    const end = [eventDay(endDay, now), notice.eventEndTime].filter(Boolean).join(", ");
    return `${start} – ${end}`;
  }
  return notice.eventEndTime ? `${start}–${notice.eventEndTime}` : start;
};

/** The Events table's Date column: "Tue 3 Nov", or "Mon 2 Nov – Wed 4 Nov" for a date range. */
export const plannerEventDate = (notice: PlannerEventFacts, now: Date = new Date()): string => {
  const startDay = toLocalDay(notice.eventStartDate);
  if (!startDay) return "";
  const endDay = toLocalDay(notice.eventEndDate);
  if (endDay && endDay.getTime() !== startDay.getTime()) {
    return `${eventDay(startDay, now)} – ${eventDay(endDay, now)}`;
  }
  return eventDay(startDay, now);
};

/** The Events table's Time column: "9:00", "18:00–20:00", or "All day" with no start time. */
export const plannerEventTime = (notice: PlannerEventFacts): string => {
  if (!notice.eventStartTime) return "All day";
  if (!notice.eventEndTime) return notice.eventStartTime;
  const endDay = toLocalDay(notice.eventEndDate);
  const startDay = toLocalDay(notice.eventStartDate);
  const spansDays = endDay && startDay && endDay.getTime() !== startDay.getTime();
  return `${notice.eventStartTime}${spansDays ? " – " : "–"}${notice.eventEndTime}`;
};

/** Whether an Event matches the Events table search: its title, place or day, ignoring case. */
export const plannerEventMatchesSearch = (
  notice: Notice,
  query: string,
  now: Date = new Date(),
): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [notice.title, notice.eventLocation, plannerEventDate(notice, now)]
    .filter(Boolean)
    .some((text) => text!.toLowerCase().includes(needle));
};

/** What Add Event on a sent Planner sends: the Event, and whether parents get a notification now. */
export type PlannerAddedEventRequest = { event: PlannerEventPayload; letParentsKnow: boolean };

/**
 * An Event added to a sent Planner, built as sending the same row in the
 * Planner would (Add Event's payload building, the Planner's audience and
 * shared Remind members). The row ID makes adding it twice harmless.
 */
export const plannerAddedEventRequest = (
  planner: SavedPlanner,
  row: PlannerRow,
  options: { letParentsKnow: boolean; defaultDriveFolderKey?: string },
): PlannerAddedEventRequest => ({
  event: plannerEventPayloads(plannerDetails(planner), [row], {
    defaultDriveFolderKey: options.defaultDriveFolderKey,
  })[0],
  letParentsKnow: options.letParentsKnow,
});

/** Beside "Let parents know": what parents get, given the Event's resolved Remind members. */
export const letParentsKnowHint = (letParentsKnow: boolean, eventReminderModes: string[]) => {
  if (letParentsKnow) {
    return eventReminderModes.length > 0
      ? "Parents get a notification about this Event now, and its Event reminders later."
      : "Parents get a notification about this Event now.";
  }
  return eventReminderModes.length > 0
    ? "No notification now. It still goes on parents' calendars, with its Event reminders."
    : "No notification now, and this Event has no Event reminders. It still goes on parents' calendars.";
};

export const addedPlannerEventMessage = (letParentsKnow: boolean) =>
  letParentsKnow
    ? "Event added. Parents have been notified."
    : "Event added to parents' calendars, without a notification.";

/**
 * What a failed Add Event tells the Team member. A dropped connection or server
 * failure may have added it; pressing Add Event again is safe, because the
 * same row can't be added twice.
 */
export const plannerAddEventFailureMessage = (error: unknown): string =>
  plannerWriteFailureMessage(error, {
    rowProblems: "Nothing was added. Fix the problems shown on the Event and try again.",
    unfinished: "The Event may not have been added. Press Add Event again: it won't be added twice.",
    refused: "Couldn't add the Event.",
  });

/** A sent Event's when and where: the only things a Team member can change after sending. */
export type PlannerEventWhenAndWhere = {
  /** YYYY-MM-DD. */
  eventStartDate: string;
  /** HH:MM. */
  eventStartTime: string;
  /** YYYY-MM-DD, or empty for no end date. */
  eventEndDate: string;
  /** HH:MM, or empty for no end time. */
  eventEndTime: string;
  eventLocation: string;
};

export type PlannerEventChangeField = keyof PlannerEventWhenAndWhere;

/** "8:30" as the time picker needs it, "08:30". */
const pickerTime = (time: string | undefined) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time?.trim() ?? "");
  return match ? `${match[1]!.padStart(2, "0")}:${match[2]}` : (time?.trim() ?? "");
};

/** A sent Event's date, time and location, ready to change. */
export const plannerEventWhenAndWhere = (notice: Notice): PlannerEventWhenAndWhere => ({
  eventStartDate: notice.eventStartDate?.trim() ?? "",
  eventStartTime: pickerTime(notice.eventStartTime),
  eventEndDate: notice.eventEndDate?.trim() ?? "",
  eventEndTime: pickerTime(notice.eventEndTime),
  eventLocation: notice.eventLocation?.trim() ?? "",
});

/** Whether the change leaves the Event as it is. */
export const isSameWhenAndWhere = (notice: Notice, change: PlannerEventWhenAndWhere) => {
  const now = plannerEventWhenAndWhere(notice);
  return (Object.keys(now) as PlannerEventChangeField[]).every(
    (field) => now[field] === change[field].trim(),
  );
};

/** "Tue 14 Apr, 17:00": when an Event's RSVPs close, or "" if it has no deadline. */
export const rsvpDeadlineText = (notice: Pick<Notice, "rsvpEnabled" | "rsvpClosesAt">): string => {
  if (!notice.rsvpEnabled || !notice.rsvpClosesAt) return "";
  const deadline = new Date(notice.rsvpClosesAt);
  if (Number.isNaN(deadline.getTime())) return "";
  const day = `${deadline.toLocaleDateString("en-GB", { weekday: "short" })} ${dayMonth(deadline, false)}`;
  const time = deadline.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${day}, ${time}`;
};

/**
 * Problems with a changed Event, in Add Event's words. An RSVP deadline that
 * would come after the moved Event shows on the field that caused it: the end
 * date, the end time, or else the start date. The deadline itself can't be
 * changed here, so the problem says what to do instead.
 */
export const plannerEventChangeProblems = (
  notice: Notice,
  change: PlannerEventWhenAndWhere,
  now: Date = new Date(),
): Partial<Record<PlannerEventChangeField, string>> => {
  const dates = {
    eventStartDate: toLocalDay(change.eventStartDate),
    eventStartTime: change.eventStartTime,
    eventEndDate: toLocalDay(change.eventEndDate),
    eventEndTime: change.eventEndTime,
  };
  const deadlineLate = rsvpDeadlineProblem({
    rsvpEnabled: notice.rsvpEnabled,
    rsvpClosesAt: notice.rsvpClosesAt,
    ...dates,
  })
    ? `RSVPs close ${rsvpDeadlineText(notice)}, after this. Pick a later date, or delete the Event and add it again with a new RSVP deadline.`
    : undefined;
  const deadlineField: PlannerEventChangeField = dates.eventEndDate
    ? "eventEndDate"
    : change.eventEndTime.trim()
      ? "eventEndTime"
      : "eventStartDate";
  const problems: Partial<Record<PlannerEventChangeField, string>> = {
    eventStartDate: eventDayProblem(dates.eventStartDate, now),
    eventStartTime: eventTimeProblem(change.eventStartTime),
    eventEndDate: eventEndProblem(dates),
    eventEndTime: eventEndTimeProblem(dates),
    eventLocation: eventPlaceProblem(change.eventLocation),
  };
  if (deadlineLate && !problems[deadlineField]) problems[deadlineField] = deadlineLate;
  for (const field of Object.keys(problems) as PlannerEventChangeField[]) {
    if (!problems[field]) delete problems[field];
  }
  return problems;
};

/** Whether the change moves the Event in time, so parents most likely need telling. */
export const changeMovesEvent = (notice: Notice, change: PlannerEventWhenAndWhere) => {
  const now = plannerEventWhenAndWhere(notice);
  return (["eventStartDate", "eventStartTime", "eventEndDate", "eventEndTime"] as const).some(
    (field) => now[field] !== change[field].trim(),
  );
};

/** Under "Let parents know" when changing an Event. */
export const changedPlannerEventHint = (letParentsKnow: boolean) =>
  letParentsKnow
    ? "Parents get a notification with the new date, time and place. Their calendars and Event reminders update too."
    : "No notification. Parents' calendars and Event reminders update without one.";

export const changedPlannerEventMessage = (letParentsKnow: boolean) =>
  letParentsKnow ? "Event changed. Parents have been notified." : "Event changed.";

/** What a failed change tells the Team member. Saving the same change twice is safe. */
export const plannerEventChangeFailureMessage = (error: unknown): string => {
  const failure = error as { status?: number; message?: string } | null;
  if (typeof failure?.status !== "number" || failure.status >= 500) {
    return "The Event may not have been changed. Press Save again: parents won't be notified twice.";
  }
  return `Couldn't change the Event.${failure.message ? ` ${failure.message}` : " Please try again."}`;
};

const COMES_OFF_CALENDARS = "comes off parents' Calendars and its Event reminders stop.";

/**
 * In the confirm before deleting one Event from a sent Planner. `when` (e.g.
 * "Fri 5 Feb, 8:00") tells apart Events with the same title, like a weekly assembly.
 */
export const plannerEventDeleteText = (
  eventTitle: string,
  eventsInPlanner: number,
  when?: string,
) => {
  const text = `“${eventTitle}”${when ? ` on ${when}` : ""} ${COMES_OFF_CALENDARS}`;
  return eventsInPlanner === 1
    ? `${text} It's the last Event in this Planner; the Planner stays, with 0 events, until you delete it.`
    : text;
};

/** In the confirm before Delete all on a sent Planner. */
export const plannerDeleteAllText = (plannerTitle: string, eventCount: number) => {
  const title = `“${plannerDisplayTitle(plannerTitle)}”`;
  if (eventCount === 0) return `${title} has no Events left. It will be removed from your Planners.`;
  const events =
    eventCount === 1
      ? `The 1 event in ${title} ${COMES_OFF_CALENDARS}`
      : `All ${eventCountLabel(eventCount)} in ${title} come off parents' Calendars and their Event reminders stop.`;
  return `${events} The Planner is deleted too.`;
};

/** Upcoming's Circle filter: every Event (Internal team only too), Events for Everyone, or a Circle's ID. */
export const UPCOMING_ALL_CIRCLES = "all";
export const UPCOMING_EVERYONE = "everyone";

/** One Event in Upcoming, tagged with its Planner's name and whether only the Team sees it. */
export type UpcomingEvent = {
  notice: Notice;
  /** Missing for an Event made with Add Event. */
  plannerTitle?: string;
  internalTeamOnly: boolean;
  /** It's on today: today falls between its first and last day. */
  today: boolean;
};

/** "November 2026" and its Events, in date order. */
export type UpcomingMonth = { month: string; events: UpcomingEvent[] };

const matchesCircleFilter = (notice: Notice, circle: string) => {
  if (circle === UPCOMING_ALL_CIRCLES) return true;
  if (circle === UPCOMING_EVERYONE) {
    return !notice.targetSubcircle && !notice.targetUserId && notice.staffOnly !== true;
  }
  return notice.targetSubcircle === circle;
};

/** The Event hasn't finished before today: its last day is today or later. */
const isFromTodayOnward = (notice: Notice, now: Date) => {
  const firstDay = toLocalDay(notice.eventStartDate);
  if (!firstDay) return false;
  const lastDay = toLocalDay(notice.eventEndDate) ?? firstDay;
  return lastDay >= new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const isOnToday = (notice: Notice, now: Date) => {
  const firstDay = toLocalDay(notice.eventStartDate);
  if (!firstDay) return false;
  const lastDay = toLocalDay(notice.eventEndDate) ?? firstDay;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return firstDay <= today && lastDay >= today;
};

/**
 * Upcoming: every Event from today onward (one still going on counts), whether
 * made with Add Event or sent in a Planner, grouped by the month it starts in.
 */
export const upcomingEventMonths = (
  notices: Notice[],
  planners: SavedPlanner[],
  options: { circle: string; now?: Date; search?: string },
): UpcomingMonth[] => {
  const now = options.now ?? new Date();
  const needle = (options.search ?? "").trim().toLowerCase();
  const plannerTitles = new Map(
    planners.map((planner) => [planner.plannerId, plannerDisplayTitle(planner.title)]),
  );
  const months: UpcomingMonth[] = [];
  notices
    .filter(
      (notice) =>
        !notice.isDeleted &&
        !notice.isDraft &&
        isFromTodayOnward(notice, now) &&
        matchesCircleFilter(notice, options.circle) &&
        (plannerEventMatchesSearch(notice, needle, now) ||
          Boolean(
            notice.plannerId && plannerTitles.get(notice.plannerId)?.toLowerCase().includes(needle),
          )),
    )
    .sort((a, b) => eventStartsAt(a) - eventStartsAt(b))
    .forEach((notice) => {
      const startDay = toLocalDay(notice.eventStartDate);
      if (!startDay) return;
      const month = startDay.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
      if (months[months.length - 1]?.month !== month) months.push({ month, events: [] });
      months[months.length - 1].events.push({
        notice,
        plannerTitle: notice.plannerId ? plannerTitles.get(notice.plannerId) : undefined,
        internalTeamOnly: notice.staffOnly === true,
        today: isOnToday(notice, now),
      });
    });
  return months;
};
